// Work-queue cards (design `workQueues`, D:9175-9306), pure. Each card summarises one
// live queue from the same list endpoints its section uses (no aggregate endpoint is
// invented) and deep-links into that section. Two design cards are deferred and swapped
// for kept queues: Review edit requests (DR-42) → the refund queue (DR-42 launch), and
// Account requests (DR-43) → subscription payments to verify (DR-39).
import type { MessageKey } from '@bookingdei/i18n/message-key';
import type {
  AdminCatalogItem,
  AdminRefund,
  Booking,
  Dispute,
  AdminBusiness,
  ReviewFlag,
  SubscriptionPayment,
} from '../../mock/seeds/schema-types';
import { APPROVAL_SLA } from '../approvals/approvals-config';
import { hoursUntil } from '../approvals/approval-rows';
import type { Fmt } from './overview-model';
import { msg, num } from './overview-model';

export interface QueueLists {
  /** Listings waiting for Business verification (API-144 `?status=pending_approval`, DR-78). */
  pendingBusinesses: AdminBusiness[];
  services: AdminCatalogItem[];
  payments: Booking[];
  disputes: Dispute[];
  flags: ReviewFlag[];
  refunds: AdminRefund[];
  subscriptionPayments: SubscriptionPayment[];
}

export type QueueTone = 'amber' | 'blue' | 'rose' | 'violet' | 'teal' | 'green';

export interface QueueCard {
  id: string;
  tone: QueueTone;
  label: string;
  count: number;
  unit: string;
  badge: string;
  urgent: boolean;
  metas: { k: string; v: string; warn?: boolean }[];
  sub: string;
  link: string;
  to: string;
}

const HOUR = 3_600_000;
// Disputes this close to their SLA count as breaching (design `slaLeft <= 4`).
const DISPUTE_BREACH_HOURS = 4;

const minOf = (xs: number[]) => (xs.length ? Math.min(...xs) : null);

function hours(f: Fmt, n: number | null): string {
  return n === null ? '—' : msg(f, 'admin.overview.q.hours', { n: Math.abs(n) });
}

const METHOD_KEY: Record<string, MessageKey> = {
  bkash: 'payment.bkash',
  nagad: 'payment.nagad',
  rocket: 'payment.rocket',
  bank: 'payment.bank',
};

/** "Nagad +1": the first method seen, plus how many others. */
function methodsLabel(f: Fmt, methods: string[]): string {
  const uniq = [...new Set(methods)];
  if (!uniq.length) return '—';
  const first = METHOD_KEY[uniq[0]!] ? f.t(METHOD_KEY[uniq[0]!]!) : uniq[0]!;
  return uniq.length > 1 ? `${first} +${num(f, uniq.length - 1)}` : first;
}

export function buildQueues(l: QueueLists, f: Fmt): QueueCard[] {
  const now = f.now.getTime();

  // ---- approvals: businesses awaiting verification + services waiting (FR-A1, FR-A19).
  // A listing row carries no submission time (API-144), so only services drive the SLA.
  const apps = l.pendingBusinesses.filter((b) => b.status === 'pending_approval');
  const services = l.services.filter(
    (s) => s.status === 'pending_review' || s.status === 'changes_requested',
  );
  const slaLeft = [
    ...services
      .filter((s) => s.status === 'pending_review' && s.submitted_at)
      .map((s) =>
        Math.round(
          (Date.parse(s.submitted_at!) + APPROVAL_SLA.REVIEW_SLA_HOURS * HOUR - now) / HOUR,
        ),
      ),
  ].filter((h): h is number => h !== null);
  const apvOverdue = slaLeft.filter((h) => h < 0).length;
  const apvNext = minOf(slaLeft.filter((h) => h >= 0));
  const onMerchant = services.filter((s) => s.status === 'changes_requested').length;

  // ---- booking payment proofs waiting for an operator (FR-A7 bookings feed)
  const pay = l.payments.filter((b) => b.status === 'payment_verification');
  const held = pay.reduce((x, b) => x + (b.total_minor ?? 0), 0);

  // ---- open disputes against their SLA window
  const dis = l.disputes.filter((d) => d.status === 'open');
  const disLeft = dis
    .filter((d) => typeof d.sla_hours === 'number')
    .map((d) => Math.round((Date.parse(d.opened_at) + d.sla_hours! * HOUR - now) / HOUR));
  const disBreach = disLeft.filter((h) => h <= DISPUTE_BREACH_HOURS).length;
  const disSoon = minOf(disLeft);
  const openIds = new Set(dis.map((d) => d.id));
  const pendingRefunds = l.refunds.filter((r) => r.status === 'pending');
  const disRefunds = pendingRefunds.filter((r) => r.dispute_id && openIds.has(r.dispute_id)).length;

  // ---- review flags
  const flags = l.flags.filter((r) => r.status === 'open');
  const auto = flags.filter((r) => r.source === 'system').length;

  // ---- refund queue (Finance approval before payout, DR-42)
  const rfValue = pendingRefunds.reduce((x, r) => x + r.amount_minor, 0);
  const rfLeft = pendingRefunds
    .map((r) => hoursUntil(r.sla_due_at, now))
    .filter((h): h is number => h !== null);
  const rfOverdue = rfLeft.filter((h) => h < 0).length;
  const rfSoon = minOf(rfLeft.filter((h) => h >= 0));

  // ---- subscription payments to verify (DR-39, under Payments)
  const subs = l.subscriptionPayments.filter((p) => p.status === 'pending');
  const subValue = subs.reduce((x, p) => x + p.amount_minor, 0);

  const cards: QueueCard[] = [
    {
      id: 'approvals',
      tone: 'amber',
      label: f.t('admin.overview.q.approvals'),
      count: apps.length + services.length,
      unit: f.t('admin.overview.q.approvals_unit'),
      urgent: apvOverdue > 0,
      badge:
        apvOverdue > 0
          ? msg(f, 'admin.overview.q.overdue_n', { n: apvOverdue })
          : f.t('admin.overview.q.within_sla'),
      metas: [
        { k: f.t('admin.overview.q.businesses'), v: num(f, apps.length) },
        { k: f.t('admin.overview.q.services'), v: num(f, services.length) },
        {
          k: f.t('admin.overview.q.next_sla'),
          v: hours(f, apvNext),
          warn: apvNext !== null && apvNext <= APPROVAL_SLA.DUE_SOON_HOURS,
        },
      ],
      sub:
        onMerchant > 0
          ? msg(f, 'admin.overview.q.approvals_merchant', { n: onMerchant })
          : f.t('admin.overview.q.approvals_team'),
      link: f.t('admin.overview.q.approvals_link'),
      to: '/approvals',
    },
    {
      id: 'payments',
      tone: 'blue',
      label: f.t('admin.overview.q.payments'),
      count: pay.length,
      unit: f.t('admin.overview.q.payments_unit'),
      urgent: pay.length > 0,
      badge: pay.length > 0 ? f.t('admin.overview.q.manual_check') : f.t('admin.overview.q.clear'),
      metas: [
        { k: f.t('admin.overview.q.value_held'), v: f.money(held) },
        {
          k: f.t('admin.overview.q.methods'),
          v: methodsLabel(
            f,
            pay.map((b) => b.proofs?.[0]?.method ?? b.pay_method),
          ),
        },
        {
          k: f.t('admin.overview.q.avg_ticket'),
          v: f.money(pay.length ? Math.round(held / pay.length) : 0),
        },
      ],
      sub: f.t('admin.overview.q.payments_sub'),
      link: f.t('admin.overview.q.payments_link'),
      to: '/bookings?status=payment_verification',
    },
    {
      id: 'disputes',
      tone: 'rose',
      label: f.t('admin.overview.q.disputes'),
      count: dis.length,
      unit: f.t('admin.overview.q.disputes_unit'),
      urgent: disBreach > 0,
      badge:
        disBreach > 0
          ? msg(f, 'admin.overview.q.breaching_n', { n: disBreach })
          : f.t('admin.overview.q.within_sla'),
      metas: [
        {
          k: f.t('admin.overview.q.escalated'),
          v: num(f, dis.filter((d) => d.escalated_at).length),
        },
        {
          k:
            disSoon !== null && disSoon < 0
              ? f.t('admin.overview.q.overdue_by')
              : f.t('admin.overview.q.nearest_sla'),
          v: hours(f, disSoon),
          warn: disSoon !== null && disSoon <= DISPUTE_BREACH_HOURS,
        },
        {
          k: f.t('admin.overview.q.refunds'),
          v: msg(f, 'admin.overview.q.pending_n', { n: disRefunds }),
        },
      ],
      sub: f.t('admin.overview.q.disputes_sub'),
      link: f.t('admin.overview.q.disputes_link'),
      to: '/disputes',
    },
    {
      id: 'review_flags',
      tone: 'violet',
      label: f.t('admin.overview.q.flags'),
      count: flags.length,
      unit: f.t('admin.overview.q.flags_unit'),
      urgent: auto > 0,
      badge: auto > 0 ? f.t('admin.overview.q.fake_signal') : f.t('admin.overview.q.manual_only'),
      metas: [
        { k: f.t('admin.overview.q.auto_flag'), v: num(f, auto) },
        {
          k: f.t('admin.overview.q.merchant_flag'),
          v: num(f, flags.filter((r) => r.source === 'merchant').length),
        },
        {
          k: f.t('admin.overview.q.reply_flag'),
          v: num(f, flags.filter((r) => r.target === 'reply').length),
        },
      ],
      sub: f.t('admin.overview.q.flags_sub'),
      link: f.t('admin.overview.q.flags_link'),
      to: '/reviews',
    },
    {
      id: 'refunds',
      tone: 'teal',
      label: f.t('admin.overview.q.refund_queue'),
      count: pendingRefunds.length,
      unit: f.t('admin.overview.q.refund_unit'),
      urgent: rfOverdue > 0,
      badge:
        pendingRefunds.length > 0
          ? f.t('admin.overview.q.finance_signoff')
          : f.t('admin.overview.q.clear'),
      metas: [
        { k: f.t('admin.overview.q.value'), v: f.money(rfValue) },
        { k: f.t('admin.overview.q.overdue'), v: num(f, rfOverdue), warn: rfOverdue > 0 },
        { k: f.t('admin.overview.q.next_sla'), v: hours(f, rfSoon) },
      ],
      sub: f.t('admin.overview.q.refund_sub'),
      link: f.t('admin.overview.q.refund_link'),
      to: '/payments?tab=refunds',
    },
    {
      id: 'subscription_payments',
      tone: 'green',
      label: f.t('admin.overview.q.subs'),
      count: subs.length,
      unit: f.t('admin.overview.q.subs_unit'),
      urgent: false,
      badge:
        subs.length > 0 ? f.t('admin.overview.q.billing_signoff') : f.t('admin.overview.q.clear'),
      metas: [
        { k: f.t('admin.overview.q.value'), v: f.money(subValue) },
        {
          k: f.t('admin.overview.q.methods'),
          v: methodsLabel(
            f,
            subs.map((p) => p.method),
          ),
        },
        {
          k: f.t('admin.overview.q.merchants'),
          v: num(f, new Set(subs.map((p) => p.business_id)).size),
        },
      ],
      sub: f.t('admin.overview.q.subs_sub'),
      link: f.t('admin.overview.q.subs_link'),
      to: '/payments',
    },
  ];
  return cards;
}
